import BaseComponent from '../../BaseComponent';
import {ComponentContext} from '../../IComponent';
import contentTranslationClient, {TranslationReference, TranslationResult, TranslationStatus} from '../ContentTranslationClient';
import {initializeYoutubeVideos} from '../../../extensions/YoutubeVideosExtension';
import truncateText from './truncateText';

const InitialTranslationState = {
    ...TranslationStatus,
    Pending: 'pending',
} as const;
type InitialTranslationState = typeof InitialTranslationState[keyof typeof InitialTranslationState];

const TranslationAction = {
    ShowOriginal: 'show-original',
    ShowTranslated: 'show-translated',
    Retry: 'retry',
} as const;
type TranslationAction = typeof TranslationAction[keyof typeof TranslationAction];
type TranslationMode = 'original' | 'translated';

const stateChangedEvent = 'content-translation-state-changed';

type TranslationField = {
    element: HTMLElement;
    name: string;
    render: string;
    truncate: number | null;
    stripHtml: boolean;
    original: DocumentFragment;
};

export default class ContentTranslation extends BaseComponent {
    private readonly root: HTMLElement;
    private readonly reference: TranslationReference | null;
    private readonly fields: TranslationField[];
    private readonly buttons: HTMLButtonElement[];
    private readonly initialState: InitialTranslationState;
    private translatedContent: Map<TranslationField, DocumentFragment> | null = null;
    private desiredMode: TranslationMode = 'translated';
    private state: InitialTranslationState;
    private disposed = false;
    private unsubscribe: (() => void) | null = null;
    private observer: IntersectionObserver | null = null;

    public constructor($component: JQuery<HTMLElement>, context?: ComponentContext) {
        super($component, context);
        this.root = $component[0];
        const {type, id, state} = this.options || {};
        if (!this.isGroup && (typeof type !== 'string' || type === '' || typeof id !== 'string' || id === '')) {
            throw new Error('Invalid content translation reference.');
        }

        this.reference = this.isGroup ? null : {type, id};
        this.initialState = state;
        this.state = state;

        const fieldElements = Array.from(this.root.querySelectorAll<HTMLElement>('[data-translation-field]'));
        if (this.root.matches('[data-translation-field]')) {
            fieldElements.unshift(this.root);
        }
        this.fields = fieldElements
            .filter(element => element.closest('[data-content-translation]') === this.root)
            .map(element => {
                const truncate = element.dataset.translationTruncate;
                const stripHtml = element.dataset.translationStripHtml;
                if (truncate !== undefined && (!/^[1-9][0-9]*$/.test(truncate)
                    || !Number.isFinite(Number(truncate)) || element.dataset.translationRender === 'html')
                    || stripHtml !== undefined && (stripHtml !== 'true' || element.dataset.translationRender !== 'text')) {
                    throw new Error('Invalid content translation field metadata.');
                }
                return {
                    element,
                    name: element.dataset.translationField,
                    render: element.dataset.translationRender,
                    truncate: truncate === undefined ? null : Number(truncate),
                    stripHtml: stripHtml === 'true',
                    original: this.copyChildren(element),
                };
            });

        this.buttons = Array.from(this.root.querySelectorAll<HTMLButtonElement>(
            Object.values(TranslationAction).map(action => `[data-translation-${action}]`).join(', '),
        )).filter(element => element.closest('[data-content-translation]') === this.root);

        if (this.fields.length === 0 && !this.isGroup) {
            throw new Error('Content translation fields are missing.');
        }

        if (this.fields.some(field => !field.name || !['text', 'textWithBreaks', 'html'].includes(field.render))) {
            throw new Error('Invalid content translation field metadata.');
        }
    }

    public init(): void {
        // Publish before notifying ancestors; ComponentFactory also retains this reference.
        this.root['echonityComponent'] = this;
        this.desiredMode = this.findGroupParent()?.desiredMode || 'translated';
        this.root.addEventListener(stateChangedEvent, this.onStateChanged);
        this.buttons.forEach(button => button.addEventListener('click', this.onClick));
        if (this.isGroup) {
            this.state = TranslationStatus.Unchanged;
        } else if (this.initialState === InitialTranslationState.Translated) {
            this.restoreServerTranslation();
        } else if (this.initialState === InitialTranslationState.Pending) {
            this.observeViewport();
        }
        this.notifyStateChanged();
    }

    public beforeRemove(): void {
        if (this.disposed) {
            return;
        }
        this.disposed = true;
        this.observer?.disconnect();
        this.observer = null;
        this.buttons.forEach(button => button.removeEventListener('click', this.onClick));
        this.root.removeEventListener(stateChangedEvent, this.onStateChanged);
        this.unsubscribe?.();
        this.unsubscribe = null;
        this.notifyStateChanged();
    }

    private readonly onClick = (event: MouseEvent): void => {
        event.preventDefault();
        const button = event.currentTarget as HTMLElement;
        if (button.hasAttribute(`data-translation-${TranslationAction.Retry}`)) {
            this.controlledBlocks().filter(block => block.state === TranslationStatus.Error)
                .forEach(block => block.request());
            return;
        }
        const mode = button.hasAttribute(`data-translation-${TranslationAction.ShowTranslated}`) ? 'translated' : 'original';
        this.controlledBlocks().forEach(block => block.setMode(mode));
    };

    private setMode(mode: TranslationMode): void {
        this.desiredMode = mode;
        if (this.translatedContent) {
            this.render(mode === 'translated' ? this.translatedContent : null);
        }
        this.notifyStateChanged();
    }

    private get controlsNested(): boolean {
        return this.root.dataset.contentTranslationNested === 'true';
    }

    private get isGroup(): boolean {
        return this.root.hasAttribute('data-content-translation-group');
    }

    private findGroupParent(): ContentTranslation | null {
        let parent = this.root.parentElement?.closest<HTMLElement>('[data-content-translation-group]');
        while (parent) {
            const component = parent['echonityComponent'];
            if (
                component instanceof ContentTranslation
                && !component.disposed
                && (!this.isGroup || component.controlsNested)
            ) {
                return component;
            }
            if (!this.isGroup) {
                return null;
            }
            parent = parent.parentElement?.closest<HTMLElement>('[data-content-translation-group]');
        }
        return null;
    }

    private controlledBlocks(): ContentTranslation[] {
        const descendants = this.isGroup
            ? Array.from(this.root.querySelectorAll<HTMLElement>('[data-content-translation]'))
                .filter(element => this.controlsNested || (!element.hasAttribute('data-content-translation-group')
                    && element.parentElement?.closest('[data-content-translation-group]') === this.root))
                .map(element => element['echonityComponent'])
                .filter((component): component is ContentTranslation => component instanceof ContentTranslation && !component.disposed)
            : [];
        return [this, ...descendants];
    }

    private readonly onStateChanged = (): void => {
        if (!this.disposed) {
            this.updateButtons();
        }
    };

    private notifyStateChanged(): void {
        this.root.dispatchEvent(new CustomEvent(stateChangedEvent, {bubbles: true}));
    }

    private observeViewport(): void {
        if (typeof IntersectionObserver === 'undefined') {
            this.request();
            return;
        }

        this.observer = new IntersectionObserver(entries => {
            if (this.observer && entries.some(entry => entry.isIntersecting)) {
                this.request();
            }
        }, {rootMargin: '300px 0px'});

        const target = this.root.matches('option')
            ? this.root.closest('select')
            : this.root.closest('[data-content-translation-group]');
        this.observer.observe(target || this.root);
    }

    private request(): void {
        if (this.disposed || this.unsubscribe || !this.reference) {
            return;
        }

        this.observer?.disconnect();
        this.observer = null;
        this.state = InitialTranslationState.Pending;
        this.notifyStateChanged();

        const sendIndividual = this.options?.individual === true;
        this.unsubscribe = contentTranslationClient.subscribe(
            this.reference,
            result => this.receive(result),
            sendIndividual,
        );
    }

    private receive(result: TranslationResult): void {
        this.unsubscribe = null;
        if (this.disposed || !this.root.isConnected) {
            return;
        }

        if (result.status === TranslationStatus.Unchanged || result.status === TranslationStatus.Unavailable) {
            this.state = result.status;
            this.notifyStateChanged();
            return;
        }

        if (result.status !== TranslationStatus.Translated || !this.prepareTranslation(result.fields)) {
            this.state = TranslationStatus.Error;
            this.notifyStateChanged();
            return;
        }

        this.state = TranslationStatus.Translated;
        this.setMode(this.desiredMode);
    }

    private prepareTranslation(values: {[name: string]: string} | undefined): boolean {
        if (!values || this.fields.some(field => typeof values[field.name] !== 'string')) {
            return false;
        }

        const translatedContent = new Map<TranslationField, DocumentFragment>();
        this.fields.forEach(field => {
            const fragment = document.createDocumentFragment();
            let value = values[field.name];
            if (field.stripHtml) {
                const template = document.createElement('template');
                template.innerHTML = value;
                value = template.content.textContent || '';
            }
            if (field.truncate !== null) {
                value = truncateText(value, field.truncate);
            }

            if (field.render === 'html') {
                // The endpoint validates HTML safety and structure before returning this field.
                const template = document.createElement('template');
                template.innerHTML = value;
                fragment.appendChild(template.content);
            } else if (field.render === 'textWithBreaks') {
                value.split(/\r\n|\r|\n/).forEach((line, index) => {
                    if (index > 0) {
                        fragment.appendChild(document.createElement('br'));
                    }
                    fragment.appendChild(document.createTextNode(line));
                });
            } else {
                fragment.appendChild(document.createTextNode(value));
            }

            translatedContent.set(field, fragment);
        });

        this.translatedContent = translatedContent;

        return true;
    }

    private render(contents: Map<TranslationField, DocumentFragment> | null): void {
        this.fields.forEach(field => {
            const fragment = contents?.get(field) || field.original;
            field.element.replaceChildren(fragment.cloneNode(true));
            if (field.render === 'html') {
                initializeYoutubeVideos($(field.element));
            }
        });
    }

    private restoreServerTranslation(): void {
        const originals = Array.from(this.root.querySelectorAll<HTMLTemplateElement>('template[data-translation-original]'))
            .filter(element => element.closest('[data-content-translation]') === this.root);
        const optionOriginal = this.root.matches('option') ? this.root.dataset.translationOptionOriginal : undefined;
        const originalByField = new Map<TranslationField, HTMLTemplateElement>();
        this.fields.forEach(field => {
            if (field.element === this.root && optionOriginal !== undefined) {
                return;
            }
            const index = originals.findIndex(element => element.dataset.translationOriginal === field.name);
            if (index < 0) {
                throw new Error('Server-rendered translation is missing its original.');
            }
            originalByField.set(field, originals.splice(index, 1)[0]);
        });

        this.translatedContent = new Map();
        this.fields.forEach(field => {
            this.translatedContent.set(field, this.copyChildren(field.element));
            if (field.element === this.root && optionOriginal !== undefined) {
                field.original = document.createDocumentFragment();
                field.original.appendChild(document.createTextNode(optionOriginal));
                return;
            }
            const original = originalByField.get(field);
            field.original = original.content.cloneNode(true) as DocumentFragment;
            original.remove();
        });
        if (this.desiredMode === 'original') {
            this.render(null);
        }
    }

    private updateButtons(): void {
        const blocks = this.controlledBlocks();
        const hasTranslation = blocks.some(block => block.translatedContent !== null);
        const hasError = blocks.some(block => block.state === TranslationStatus.Error);
        const action = this.desiredMode === 'translated' ? TranslationAction.ShowOriginal : TranslationAction.ShowTranslated;
        this.buttons.forEach(button => {
            button.hidden = !(hasTranslation && button.hasAttribute(`data-translation-${action}`))
                && !(hasError && button.hasAttribute(`data-translation-${TranslationAction.Retry}`));
        });
    }

    private copyChildren(element: HTMLElement): DocumentFragment {
        const fragment = document.createDocumentFragment();
        Array.from(element.childNodes).forEach(node => fragment.appendChild(node.cloneNode(true)));
        return fragment;
    }
}
